import { Container, Stack, Divider, Grid, Typography, Paper, Box, Button, TextField, IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, List, ListItem, ListItemText } from '@mui/material';
import { DataGridPremium, GridFooter, GridFooterContainer, GridActionsCellItem } from '@mui/x-data-grid-premium';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import ThermoAspireNotificationsForm from 'forms/ThermoAspireNotificationsForm';
import { Save, Visibility, VisibilityOff, Delete, Upload } from '@mui/icons-material';

function SettingsSection({ title, helperText, children }) {
  return (
    <Grid container role="region" aria-label={title}>
      <Grid size={{ xs: 12, md: 3 }}>
        <Typography variant="h6">{title}</Typography>
        {helperText && <Typography variant="body2" color="textSecondary">{helperText}</Typography>}
      </Grid>
      <Grid size={{ xs: 12, md: 9 }}>
        {children}
      </Grid>
    </Grid>
  )
}

const DATETIME_COLUMN_PARAMS = {
  type: 'dateTime',
  valueGetter: (value) => value ? new Date(value) : null,
  renderCell: ({ value }) => value ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2">{moment(value).fromNow()}</Typography>
      <Typography variant="caption" color="textSecondary">{moment(value).format('M/D/YY h:mm:ss a')}</Typography>
    </Box>
  ) : '',
  width: 150,
}

function CustomFooterComponent({ disabled, onSubmit }) {
  return (
    <GridFooterContainer>
      <Box sx={{ p: 2 }}>
        <Button variant="contained" size="small" disabled={disabled} onClick={onSubmit}>Grant Access</Button>
      </Box>
      <GridFooter sx={{
        border: 'none', // To delete double border
        flex: 1,
      }} />
    </GridFooterContainer>
  );
}

function Admin() {
  const [data, setData] = useState(null)
  const [selectionModel, setSelectionModel] = useState({
    type: 'include',
    ids: new Set([]),
  })
  // TODO: already know whether user is admin when rendering this page, so can hide this component entirely if not admin so the admin api isn't exposed
  const updateData = () => 
    axios.get('/api/admin/users')
      .then(({data: {users}}) => setData(users))
      .catch((err) => {
        if (err.response?.status !== 403)
          console.error(err)
      })
  useEffect(() => {
    updateData()
  }, [])
  const handleGrantAccess = () => {
    axios.post('/api/admin/grantAccess', {user_ids: [...selectionModel.ids]})
      .then(({data: {success, msg}}) => {
        if (!success)
          alert(msg)
        else
          updateData().finally(() => alert(msg))
      })
  }
  if (!data) return null
  return (
    <SettingsSection title="Admin">
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h7" gutterBottom>Users</Typography>
        <DataGridPremium
          rows={data || []}
          loading={data == null}
          columns={[
            { field: 'username', headerName: 'Username', minWidth: 150 },
            { field: 'has_access', headerName: 'Access', type: 'boolean', width: 40 },
            { field: 'is_admin', headerName: 'Admin', type: 'boolean', width: 40 },
            { field: 'created_at', headerName: 'Created At', ...DATETIME_COLUMN_PARAMS },
            {
              field: 'access_granted_at',
              headerName: 'Access Granted At',
              ...DATETIME_COLUMN_PARAMS,
            },
            {
              field: 'recent_successful_login_at',
              headerName: 'Recent Successful Login At',
              ...DATETIME_COLUMN_PARAMS,
            },
            { field: 'successful_login_count', headerName: 'Successful Login Count', type: 'number', flex: 0.5 },
            {
              field: 'recent_failed_login_at',
              headerName: 'Recent Failed Login At',
              ...DATETIME_COLUMN_PARAMS,
            },
            { field: 'failed_login_count', headerName: 'Failed Login Count', type: 'number', flex: 0.5 },
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'created_at', sort: 'desc' }],
            },
          }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
          slots={{
            footer: CustomFooterComponent,
          }}
          slotProps={{ footer: { disabled: selectionModel.ids.size === 0, onSubmit: handleGrantAccess } }}
        />
      </Paper>
    </SettingsSection>
  )
}

function SequenceAnnotations() {
  const [data, setData] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [annotation, setAnnotation] = useState({name: '', sequence: '', sequence_type: 'DNA'})
  const [sequenceTypeSelected, setSequenceTypeSelected] = useState(false)
  const loadData = () => axios.get('/api/wetlab/plasmids/annotations').then(({data: {data}}) => setData(data))
  useEffect(() => { loadData() }, [])
  const processRowUpdate = async (updatedRow, originalRow) => {
    const changes = Object.fromEntries(Object.entries(updatedRow).filter(([key, value]) => value !== originalRow[key]))
    if (Object.keys(changes).length > 0)
      await axios.put('/api/wetlab/plasmids/annotations', {id: updatedRow.id, ...changes})
    return updatedRow
  }
  const handleDelete = (id) => axios.delete('/api/wetlab/plasmids/annotations', {data: {ids: [id]}})
    .then(() => setData(previous => previous.filter(annotation => annotation.id !== id)))
  const handleImport = (event) => {
    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)
    axios.post('/api/wetlab/plasmids/annotations', formData)
      .then(({data: {ids}}) => { event.target.value = ""; loadData(); alert(`Imported ${ids.length} annotation${ids.length === 1 ? "" : "s"}`) })
      .catch(error => alert(error.response?.data?.error || "Unable to import annotations"))
  }
  const handleAdd = (event) => {
    event.preventDefault()
    axios.post('/api/wetlab/plasmids/annotations', annotation)
      .then(() => {
        setAnnotation({name: '', sequence: '', sequence_type: 'DNA'})
        setSequenceTypeSelected(false)
        setFormOpen(false)
        loadData()
      })
      .catch(error => alert(error.response?.data?.error || "Unable to add annotation"))
  }
  const handleSequenceChange = (event) => {
    const sequence = event.target.value
    const normalized = sequence.replace(/\s/g, "").toUpperCase()
    const dnaCharacters = normalized.match(/[ATGC]/g)?.length || 0
    const isMostlyDna = normalized.length > 0 && dnaCharacters / normalized.length >= 0.8
    setAnnotation({
      ...annotation,
      sequence,
      ...(sequenceTypeSelected ? {} : {sequence_type: isMostlyDna ? "DNA" : "PROTEIN"}),
    })
  }
  const openAddForm = () => {
    setAnnotation({name: '', sequence: '', sequence_type: 'DNA'})
    setSequenceTypeSelected(false)
    setFormOpen(true)
  }
  return (
    <SettingsSection title="Plasmid annotations" helperText="Sequences matched against plasmid maps are managed here.">
      <Paper sx={{padding: 2}}>
        <Button variant="contained" size="small" onClick={openAddForm} sx={{mb: 1}}>Add annotation</Button>
        <DataGridPremium
          rows={data || []} loading={data == null} hideFooter disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          columns={[
            {field: 'name', headerName: 'Name', editable: true, flex: 1, minWidth: 150},
            {field: 'sequence', headerName: 'Sequence', editable: true, flex: 2, minWidth: 220},
            {field: 'sequence_type', headerName: 'Type', editable: true, width: 120, type: 'singleSelect', valueOptions: ['DNA', 'PROTEIN']},
            {field: 'actions', type: 'actions', width: 60, getActions: params => [
              <GridActionsCellItem key="delete" icon={<Delete />} label="Delete" onClick={() => handleDelete(params.id)} />,
            ]},
          ]}
        />
      </Paper>
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <form onSubmit={handleAdd}>
          <DialogTitle>Add plasmid annotation</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{pt: 1}}>
              <TextField
                label="Name"
                required
                value={annotation.name}
                onChange={event => setAnnotation({...annotation, name: event.target.value})}
              />
              <TextField
                label="Sequence"
                required
                multiline
                minRows={3}
                value={annotation.sequence}
                onChange={handleSequenceChange}
              />
              <FormControl>
                <FormLabel>Sequence type</FormLabel>
                <RadioGroup
                  row
                  value={annotation.sequence_type}
                  onChange={event => {
                    setSequenceTypeSelected(true)
                    setAnnotation({...annotation, sequence_type: event.target.value})
                  }}
                >
                  <FormControlLabel value="DNA" control={<Radio />} label="DNA" />
                  <FormControlLabel value="PROTEIN" control={<Radio />} label="Protein" />
                </RadioGroup>
              </FormControl>
              <Box>
                <Button component="label" startIcon={<Upload />}>
                  Import CSV instead
                  <input type="file" accept=".csv,text/csv" hidden onChange={event => {
                    handleImport(event)
                    setFormOpen(false)
                  }} />
                </Button>
                <Typography variant="body2" color="textSecondary">
                  CSV columns: name, sequence, sequence_type (DNA or PROTEIN)
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </SettingsSection>
  )
}

function DataSharing() {
  const [users, setUsers] = useState([])
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    axios.get('/api/settings/data-sharing')
      .then(({data}) => setUsers(data.users))
      .catch((err) => {
        if (err.response?.status !== 403)
          console.error(err)
      })
  }, [])

  const handleSubmit = () => {
    const trimmedUsername = username.trim()
    if (!trimmedUsername)
      return
    setError('')
    axios.post('/api/settings/data-sharing', {username: trimmedUsername})
      .then(({data}) => {
        setUsers([...users, data.user].sort((a, b) => a.username.localeCompare(b.username)))
        setUsername('')
      })
      .catch((err) => setError(err.response?.data?.msg || 'Failed to share data.'))
  }

  const removeUser = (userId) => {
    setError('')
    axios.delete('/api/settings/data-sharing', {data: {user_id: userId}})
      .then(() => setUsers(users.filter(user => user.id !== userId)))
      .catch((err) => setError(err.response?.data?.msg || 'Failed to remove access.'))
  }

  return (
    <Paper sx={{ padding: 2 }}>
      <Stack spacing={2}>
        <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} style={{ display: 'flex', flex: 1 }}>
          <TextField
            size="small"
            label="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            sx={{ mr: 1 }}
          />
          <Button variant="contained" type="submit" disabled={!username.trim()}>Add</Button>
        </form>
        {error && <Typography color="error">{error}</Typography>}
        <List dense disablePadding>
          {users?.length ? users.map(user => (
            <ListItem
              key={user.id}
              secondaryAction={(
                <IconButton edge="end" aria-label={`Remove ${user.username}`} onClick={() => removeUser(user.id)}>
                  <Delete />
                </IconButton>
              )}
            >
              <ListItemText primary={user.username} />
            </ListItem>
          )) : <Typography color="textSecondary">No users have access.</Typography>} 
        </List>
      </Stack>
    </Paper>
  )
}

export default function Settings({isAdmin}) {
  const SAVED_PASSWORD_MASK = '••••••••'
  const [quintara, setQuintara] = useState({username: '', password: '', po_number: '', has_password: false})
  const [showQuintaraPassword, setShowQuintaraPassword] = useState(false)
  const [quintaraSaved, setQuintaraSaved] = useState(false)
  const [passwords, setPasswords] = useState({current_password: '', new_password: '', confirm_password: ''})
  const [passwordMessage, setPasswordMessage] = useState(null)
  const [passwordSaving, setPasswordSaving] = useState(false)
  useEffect(() => {
    axios.get('/api/settings/quintara').then(({data}) => setQuintara({...data, password: data.has_password ? SAVED_PASSWORD_MASK : ''}))
  }, [])
  const saveQuintara = () => axios.post('/api/settings/quintara', {
    ...quintara,
    password: quintara.password === SAVED_PASSWORD_MASK ? '' : quintara.password,
  }).then(({data}) => {
    setQuintara({...data, password: data.has_password ? SAVED_PASSWORD_MASK : ''})
    setQuintaraSaved(true)
  })
  const changePassword = (event) => {
    event.preventDefault()
    setPasswordMessage(null)
    setPasswordSaving(true)
    axios.post('/api/settings/password', passwords)
      .then(({data}) => {
        setPasswords({current_password: '', new_password: '', confirm_password: ''})
        setPasswordMessage({severity: 'success', text: data.msg})
      })
      .catch(error => setPasswordMessage({
        severity: 'error',
        text: error.response?.data?.msg || "Unable to change password",
      }))
      .finally(() => setPasswordSaving(false))
  }
  return (
    <Container sx={{ paddingTop: 4, paddingBottom: 4 }}>
      <Stack spacing={4} divider={<Divider flexItem />}>
        <SettingsSection title="Connections">
          <Paper sx={{ padding: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Quintara</Typography>
              <TextField size="small" label="Username" value={quintara.username} onChange={e => {
                setQuintara({...quintara, username: e.target.value})
                setQuintaraSaved(false)
              }} />
              <TextField
                label="Password"
                size="small"
                type={showQuintaraPassword ? "text" : "password"}
                value={quintara.password}
                onFocus={() => {
                  if (quintara.password === SAVED_PASSWORD_MASK)
                    setQuintara({...quintara, password: ''})
                }}
                onChange={e => {
                  setQuintara({...quintara, password: e.target.value})
                  setQuintaraSaved(false)
                }}
                helperText={quintara.has_password ? "A saved password exists; enter a new one to replace it." : ""}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showQuintaraPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowQuintaraPassword(prev => !prev)}
                          edge="end"
                          sx={{ color: 'text.secondary' }}
                          disabled={quintara.has_password && quintara.password === SAVED_PASSWORD_MASK}
                        >
                          {showQuintaraPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField size="small" label="Purchase order number" value={quintara.po_number} onChange={e => {
                setQuintara({...quintara, po_number: e.target.value})
                setQuintaraSaved(false)
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="contained" size="small" startIcon={<Save />} onClick={saveQuintara}>
                  Save
                </Button>
                {quintaraSaved && <Typography color="success.main">Saved</Typography>}
              </Box>
            </Stack>
          </Paper>
        </SettingsSection>
        <SettingsSection title="Change password">
          <Paper sx={{ padding: 2 }}>
            <Stack component="form" spacing={2} onSubmit={changePassword}>
              {['current_password', 'new_password', 'confirm_password'].map((field) => (
                <TextField
                  key={field}
                  size="small"
                  type="password"
                  label={{
                    current_password: "Current password",
                    new_password: "New password",
                    confirm_password: "Confirm new password",
                  }[field]}
                  value={passwords[field]}
                  onChange={event => {
                    setPasswords({...passwords, [field]: event.target.value})
                    setPasswordMessage(null)
                  }}
                />
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="contained" size="small" type="submit" disabled={passwordSaving}>
                  Change password
                </Button>
                {passwordMessage && (
                  <Typography color={`${passwordMessage.severity}.main`}>
                    {passwordMessage.text}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        </SettingsSection>
        <SettingsSection title="Sharing">
          <DataSharing />
        </SettingsSection>
        {Admin()}
        <SequenceAnnotations />
        <SettingsSection title="Notifications">
          <ThermoAspireNotificationsForm isAdmin={isAdmin} />
          <Button onClick={() => {
            axios.post('/api/push/send')
          }}>Send Test Notification</Button>
        </SettingsSection>
        <SettingsSection title="Logout">
          <Button variant="contained" color="error" href="/api/logout">Logout</Button>
        </SettingsSection>
      </Stack>
    </Container>
  )
}
